import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import { SeverityLevel } from "@microsoft/applicationinsights-common";
import { ReactPlugin } from "@microsoft/applicationinsights-react-js";
import { History } from "history";
import { AppSettingsService } from "../../settings/services/AppSettingsService";
import { getEnv } from "../../env";
import { IAppInsightsSettings } from "../../settings/types/IAppInsightsSettings";


export interface IAppInsightsService {
    appInsights: ApplicationInsights | null;
    settings: IAppInsightsSettings | null;
    initialize: (browserHistory: History) => Promise<void>;
    trackException: (
        error: unknown,
        options?: {
            properties?: Record<string, any>;
            severityLevel?: SeverityLevel;
        }
    ) => void;
    trackPageView: (
        name?: string,
        uri?: string,
        properties?: Record<string, any>
    ) => void;
    trackEvent: (name: string, properties?: Record<string, any>) => void;
    setAuthenticatedUser: (email: string) => void;
    clearAuthenticatedUser: () => void;
}

const normalizeError = (error: unknown): Error => {
    if (error instanceof Error) {
        return error;
    }

    if (typeof error === "string") {
        return new Error(error);
    }

    try {
        return new Error(JSON.stringify(error));
    } catch {
        return new Error("Unknown error");
    }
};

export const AppInsightsService: IAppInsightsService = {
    appInsights: null,
    settings: null,

    initialize: async function (browserHistory: History): Promise<void> {
        try {
            const env = await getEnv();
            if (env.DEV) return;

            const settings = await AppSettingsService.getAppInsightsSettings();
            this.settings = settings;

            if (!settings?.configuration?.enabled) {
                return;
            }

            const instrumentationKey = settings?.instrumentationKey;

            if (!instrumentationKey) {
                console.warn("No instrumentation key found, skipping Application Insights initialization.");
                return;
            }

            const reactPlugin = new ReactPlugin();
            this.appInsights = new ApplicationInsights({
                config: {
                    instrumentationKey,
                    extensions: [reactPlugin as unknown as any],
                    extensionConfig: {
                        [reactPlugin.identifier]: {
                            history: browserHistory
                        }
                    }
                }
            });

            this.appInsights.loadAppInsights();
        }
        catch (error)
        {
            console.log("Error initializing Application Insights:", error);
        }
    },

    trackException: function (
        error: unknown,
        options?: {
            properties?: Record<string, any>;
            severityLevel?: SeverityLevel;
        }
    ): void {
        try {
            if (!this.appInsights || !this.settings?.configuration?.tracking?.exception) {
                return;
            }

            this.appInsights.trackException({
                exception: normalizeError(error),
                severityLevel: options?.severityLevel ?? SeverityLevel.Error,
                properties: options?.properties,
            });
        } catch (trackingError) {
            console.error("Error tracking exception with Application Insights:", trackingError);
        }
    },

    trackPageView: function (
        name?: string,
        uri?: string,
        properties?: Record<string, any>
    ): void {
        try {
            if (!this.appInsights || !this.settings?.configuration?.tracking?.pageView) {
                return;
            }

            const finalName = name ?? window.location.pathname;
            const finalUri = uri ?? window.location.href;

            this.appInsights.trackPageView({
                name: finalName,
                uri: finalUri,
                properties
            });
        } catch (trackingError) {
            console.error("Error tracking page view with Application Insights:", trackingError);
        }
    },

    trackEvent: function (name: string, properties?: Record<string, any>): void {
        try {
            if (!this.appInsights || !this.settings?.configuration?.tracking?.event) {
                return;
            }

            this.appInsights.trackEvent(
                {
                    name
                },
                properties
            );
        } catch (trackingError) {
            console.error("Error tracking event with Application Insights:", trackingError);
        }
    },

    setAuthenticatedUser: function (email: string): void {
        try {
            if (!this.appInsights || !email || !this.settings?.configuration?.tracking?.login) {
                return;
            }

            if (this.appInsights.context?.user?.authenticatedId === email) {
                return;
            }

            this.appInsights.setAuthenticatedUserContext(email);
        } catch (error) {
            console.error("Error setting authenticated user context in Application Insights:", error);
        }
    },

    clearAuthenticatedUser: function (): void {
        try {
            if (!this.appInsights) {
                return;
            }

            this.appInsights.clearAuthenticatedUserContext();
        } catch (error) {
            console.error("Error clearing authenticated user context in Application Insights:", error);
        }
    }
};